import React from "react";
import { Link } from "react-router-dom";
import { FolderOpen, Clock, FileCheck } from "lucide-react";
import SEO from "../components/SEO";

export const RecordKeepingUK = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Record Keeping Requirements for UK Businesses (2026 Guide)",
    "description": "Complete guide to UK business record keeping requirements. What to keep, how long (5-6 years), digital vs paper, HMRC compliance.",
    "author": { "@type": "Organization", "name": "Brightson Accounting" },
    "publisher": { "@type": "Organization", "name": "Brightson Accounting" },
    "datePublished": "2026-04-06"
  };

  return (
    <>
      <SEO
        title="Record Keeping Requirements UK 2026 | HMRC | Brightson Accounting"
        description="UK business record keeping requirements: what to keep, how long (5-6 years), digital records, invoices, receipts. HMRC compliance guide from Wolverhampton accountants."
        canonical="/blog/record-keeping-uk"
        schema={articleSchema}
      />

      <article className="blog-article">
        <header className="article-header">
          <div className="container">
            <div className="article-meta">
              <span className="article-category"><FolderOpen size={16} /> Staying Compliant</span>
              <span className="article-date">6 April 2026</span>
              <span className="article-read-time">5 min read</span>
            </div>

            <h1>Record Keeping Requirements for UK Businesses (2026 Guide)</h1>
            <p className="article-subtitle">
              What records to keep, how long to keep them, and why HMRC expects proper documentation
            </p>

            <div className="article-author">
              <div className="author-info">
                <strong>Brightson Accounting (FCCA Qualified)</strong>
                <span>Compliance Specialists | Wolverhampton</span>
              </div>
            </div>
          </div>
        </header>

        <section className="article-content">
          <div className="container">
            <div className="article-body">
              
              <p className="article-lead">
                HMRC expects you to keep proper records of all business transactions. At Brightson Accounting, we help businesses across Wolverhampton and the West Midlands maintain compliant record-keeping systems.
              </p>

              <p>
                Poor record keeping leads to:
              </p>
              <ul>
                <li>HMRC penalties (if you can't prove your figures)</li>
                <li>Overpaying tax (missing allowable expenses)</li>
                <li>Stress during tax return season</li>
                <li>Disallowed expense claims (no receipt = no claim)</li>
              </ul>

              <div className="info-box">
                <strong>Quick Summary</strong>
                <ul>
                  <li>Keep records for 5 years (Self Assessment) or 6 years (Corporation Tax, VAT)</li>
                  <li>Must be accurate, complete, and readable</li>
                  <li>Digital records becoming mandatory (Making Tax Digital)</li>
                  <li>Include: invoices, receipts, bank statements, payroll records</li>
                </ul>
              </div>

              <h2>What Records Must You Keep?</h2>

              <h3>For All Businesses:</h3>

              <p><strong>Sales Records:</strong></p>
              <ul>
                <li>All sales invoices</li>
                <li>Sales receipts and till rolls</li>
                <li>Bank deposit slips</li>
                <li>Online payment confirmations</li>
              </ul>

              <p><strong>Purchase Records:</strong></p>
              <ul>
                <li>All supplier invoices</li>
                <li>Receipts for expenses</li>
                <li>Credit card statements (business account)</li>
                <li>Petty cash records</li>
              </ul>

              <p><strong>Banking Records:</strong></p>
              <ul>
                <li>Business bank statements</li>
                <li>Paying-in books</li>
                <li>Cheque stubs</li>
                <li>Online banking transaction history</li>
              </ul>

              <h3>If You Have Employees:</h3>

              <ul>
                <li>Payroll records (salaries, deductions, PAYE)</li>
                <li>P60s issued to employees</li>
                <li>P11D forms (benefits in kind)</li>
                <li>Expense claim records</li>
                <li>Employment contracts</li>
              </ul>

              <h3>If You're VAT Registered:</h3>

              <ul>
                <li>VAT invoices (issued and received)</li>
                <li>VAT account showing VAT charged and reclaimed</li>
                <li>Copies of VAT returns</li>
                <li>Import/export documentation</li>
              </ul>

              <p>Full guide: <Link to="/blog/vat-compliance-mistakes">VAT Compliance: Common Mistakes</Link></p>

              {/* Mid-Article CTA */}
              <div className="article-cta-box" style={{ background: '#f8f9fa', border: '2px solid #0066cc', marginTop: '2rem', marginBottom: '2rem' }}>
                <h3>💡 Want to see how much tax you could save?</h3>
                <p>Most UK business owners are overpaying without realising it.</p>
                <Link to="/corporation-tax-calculator" className="article-cta-button">
                  👉 Try the Corporation Tax Calculator
                </Link>
              </div>

              <h2>How Long to Keep Records</h2>

              <table style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Tax Type</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Keep For</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Self Assessment</td>
                    <td style={{ padding: '0.75rem' }}>5 years from 31 January filing deadline</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Corporation Tax</td>
                    <td style={{ padding: '0.75rem' }}>6 years from end of accounting period</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>VAT</td>
                    <td style={{ padding: '0.75rem' }}>6 years (or longer if HMRC requests)</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}>PAYE</td>
                    <td style={{ padding: '0.75rem' }}>3 years from end of tax year</td>
                  </tr>
                </tbody>
              </table>

              <p>
                <strong>Example (Self Assessment):</strong>
              </p>
              <p>
                Tax year 2025/26, filed 31 January 2027 → Keep records until 31 January 2032.
              </p>

              <p className="highlight-note">
                When in doubt, keep records for 6 years. This covers most scenarios and protects you if HMRC enquires.
              </p>

              <h2>Digital vs Paper Records</h2>

              <h3>Paper Records</h3>

              <p><strong>Acceptable, but:</strong></p>
              <ul>
                <li>Risk of loss (fire, flood, damage)</li>
                <li>Harder to search and organize</li>
                <li>Takes up physical space</li>
                <li>Not compatible with Making Tax Digital</li>
              </ul>

              <h3>Digital Records (Recommended)</h3>

              <p><strong>Benefits:</strong></p>
              <ul>
                <li>Searchable and organized</li>
                <li>Backed up (cloud storage prevents loss)</li>
                <li>Required for Making Tax Digital (MTD)</li>
                <li>Easier to share with accountant</li>
                <li>Less storage space needed</li>
              </ul>

              <p>
                <strong>How to Digitize:</strong>
              </p>
              <ul>
                <li>Photo receipts with your phone (apps like Dext, Receipt Bank)</li>
                <li>Scan paper documents to PDF</li>
                <li>Store in cloud (Dropbox, Google Drive, OneDrive)</li>
                <li>Use accounting software that stores records automatically</li>
              </ul>

              <p>Full guide: <Link to="/blog/making-tax-digital-compliance">Making Tax Digital: What You Must Do</Link></p>

              <h2>What Makes a Valid Invoice?</h2>

              <p>For HMRC to accept an expense claim, your invoice/receipt must include:</p>

              <ul>
                <li><strong>Supplier name and address</strong></li>
                <li><strong>Date of purchase</strong></li>
                <li><strong>Description of goods/services</strong></li>
                <li><strong>Amount paid</strong></li>
                <li><strong>VAT amount</strong> (if VAT registered)</li>
                <li><strong>VAT number</strong> (if claiming VAT back)</li>
              </ul>

              <p>
                <strong>Not valid:</strong>
              </p>
              <ul>
                <li>Credit card statements alone (not detailed enough)</li>
                <li>Handwritten notes without supporting receipt</li>
                <li>Till receipts that have faded (photo them immediately!)</li>
              </ul>

              <h2>Common Record-Keeping Mistakes</h2>

              <h3>Mistake 1: Not Keeping Receipts</h3>
              <p>
                "I forgot to get a receipt" = HMRC will disallow the expense claim. Always get a receipt, even for small purchases.
              </p>

              <h3>Mistake 2: Losing Digital Records</h3>
              <p>
                Storing everything on one computer with no backup = risk of losing everything. Use cloud storage with automatic backups.
              </p>

              <h3>Mistake 3: Mixing Personal and Business</h3>
              <p>
                Keeping personal and business records together makes it impossible to prove business-only expenses. Separate from day one.
              </p>

              <h3>Mistake 4: Throwing Away Records Too Early</h3>
              <p>
                Many West Midlands businesses we see throw away records after 1-2 years. HMRC can enquire up to 6 years back — keep everything.
              </p>

              <p>Full guide: <Link to="/blog/bookkeeping-compliance-uk">Bookkeeping Basics for Compliance</Link></p>

              <h2>What Happens If You Don't Keep Records?</h2>

              <p>If HMRC enquires and you can't provide records:</p>

              <ul>
                <li>HMRC will <strong>estimate your income</strong> (usually higher than reality)</li>
                <li>They will <strong>disallow expense claims</strong> (no proof = no claim)</li>
                <li>You'll pay <strong>more tax</strong> than you owe</li>
                <li>Penalties apply for <strong>inadequate records</strong> (up to £3,000 per tax year)</li>
              </ul>

              <h2>Best Practices for Record Keeping</h2>

              <div className="info-box" style={{ background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                <strong>✅ Record Keeping Checklist</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li>Keep all receipts and invoices (digital or paper)</li>
                  <li>Photo receipts immediately (before they fade)</li>
                  <li>Use accounting software to organize records</li>
                  <li>Back up digital records to cloud storage</li>
                  <li>Separate business and personal records</li>
                  <li>Keep records for 6 years minimum</li>
                  <li>Review records quarterly (catch errors early)</li>
                </ul>
              </div>

              <h2>How Brightson Accounting Can Help</h2>

              <p>We help businesses across Wolverhampton and the West Midlands with:</p>

              <ul>
                <li>Setting up digital record-keeping systems</li>
                <li>Recommending best accounting software for your business</li>
                <li>Training you to maintain compliant records</li>
                <li>Organizing existing records (we can clean up the mess!)</li>
                <li>Handling HMRC enquiries (if records are questioned)</li>
              </ul>

              <p>
                Good record keeping = easy tax returns and full expense claims. Want to stay compliant and <Link to="/corporation-tax-calculator">reduce your corporation tax</Link>? <Link to="/contact">Speak to an accountant</Link> today.
              </p>

            </div>

            {/* End CTA */}
            <div className="article-cta-box" style={{ marginTop: '3rem' }}>
              <h2>🚀 Ready to stay compliant and reduce your tax bill?</h2>
              <p>We help businesses across Wolverhampton and the West Midlands:</p>
              <ul>
                <li>Stay compliant with HMRC</li>
                <li>Reduce tax legally</li>
                <li>Avoid penalties and investigations</li>
              </ul>
              <Link to="/contact" className="article-cta-button">👉 Book a Free Consultation</Link>
            </div>

            <div className="related-articles" style={{ marginTop: '3rem' }}>
              <h3>Related Guides</h3>
              <div className="related-links">
                <Link to="/blog/hmrc-compliance-guide-uk" className="related-link">HMRC Compliance Guide</Link>
                <Link to="/blog/bookkeeping-compliance-uk" className="related-link">Bookkeeping Compliance</Link>
                <Link to="/blog/making-tax-digital-compliance" className="related-link">Making Tax Digital</Link>
                <Link to="/contact" className="related-link">Contact</Link>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default RecordKeepingUK;